import toast from 'react-hot-toast';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '../components/Navigation';
import UserProfileModal from '../components/UserProfileModal';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Search } from 'lucide-react';
import SEO from '../components/SEO';
import { usePageView } from '../hooks/usePageView';
import styles from './Friends.module.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Friends = ({ user, setUser }) => {
  usePageView('friends');
  const [following, setFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [currentTab, setCurrentTab] = useState('following');
  const [showUnblockConfirm, setShowUnblockConfirm] = useState(false);
  const [userToUnblock, setUserToUnblock] = useState(null);

  useEffect(() => {
    loadFollowing();
    loadFollowers();
    loadBlocked();
  }, []);

  const loadFollowing = async () => {
    try {
      const response = await axios.get(`${API}/follows/following`, { withCredentials: true });
      setFollowing(response.data);
    } catch (error) {
      console.error('Error loading following:', error);
    }
  };

  const loadFollowers = async () => {
    try {
      const response = await axios.get(`${API}/follows/followers`, { withCredentials: true });
      setFollowers(response.data);
    } catch (error) {
      console.error('Error loading followers:', error);
    }
  };

  const loadBlocked = async () => {
    try {
      const response = await axios.get(`${API}/blocks`, { withCredentials: true });
      setBlocked(response.data);
    } catch (error) {
      console.error('Error loading blocked users:', error);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.trim().length < 4) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await axios.get(`${API}/users/search?q=${encodeURIComponent(query)}`, {
        withCredentials: true
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  const handleFollow = async (userId) => {
    try {
      await axios.post(`${API}/follows/${userId}`, {}, { withCredentials: true });
      loadFollowing();
      // Remove from search results or update UI
      setSearchResults(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      console.error('Error following user:', error);
      toast.error(error.response?.data?.detail || 'Error following user');
    }
  };

  const handleUnfollow = async (userId) => {
    try {
      await axios.delete(`${API}/follows/${userId}`, { withCredentials: true });
      loadFollowing();
    } catch (error) {
      console.error('Error unfollowing user:', error);
    }
  };

  const handleUnblock = async () => {
    if (!userToUnblock) return;
    
    console.log('Unblock confirmed for user ID:', userToUnblock);
    setShowUnblockConfirm(false);
    
    console.log('Calling DELETE /api/blocks/' + userToUnblock);
    
    try {
      const response = await axios.delete(`${API}/blocks/${userToUnblock}`, { withCredentials: true });
      console.log('Unblock response:', response.data);
      
      // Reload the blocked users list to update UI
      await loadBlocked();
      
      toast.success('User unblocked successfully');
    } catch (error) {
      console.error('Error unblocking user:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || 'Error unblocking user');
    } finally {
      setUserToUnblock(null);
    }
  };

  const isFollowing = (userId) => {
    return following.some(f => f.id === userId);
  };

  return (
    <>
      <SEO 
        title="Find Your Flock - Friends | baad.tv"
        description="Connect with TV show enthusiasts. Follow friends, share reviews, and discover what your flock is watching. Build your social TV community."
        url="/friends"
      />
      <div className={styles.friendsContainer}>
        <Navigation user={user} setUser={setUser} currentPage="friends" />
      
      <div className={styles.friendsContent}>
        <div className={styles.pageHeader}>
          <h1 data-testid="friends-title">Find Your Flock</h1>
          <p>Connect with your BaadTV Flock</p>
        </div>

        <Card className={styles.searchCard}>
          <CardContent className={styles.searchContent}>
            <div className={styles.searchBox}>
              <Search className={styles.searchIcon} />
              <Input
                data-testid="search-input"
                type="text"
                placeholder="Search users by handle (min. 4 characters)..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className={styles.searchInput}
              />
            </div>

            {searchResults.length > 0 && (
              <div className={styles.searchResults} data-testid="search-results">
                {searchResults.map(searchUser => (
                  <div key={searchUser.id} className={styles.userItem} data-testid={`search-user-${searchUser.id}`}>
                    <div className={styles.userInfo}>
                      <Avatar>
                        <AvatarImage src={searchUser.picture} alt={searchUser.name} />
                        <AvatarFallback>{searchUser.name[0]}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className={styles.userName}>{searchUser.name}</div>
                        <div className={styles.userUsername}>@{searchUser.username}</div>
                      </div>
                    </div>
                    {isFollowing(searchUser.id) ? (
                      <Button
                        data-testid={`unfollow-btn-${searchUser.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnfollow(searchUser.id)}
                      >
                        Unfollow
                      </Button>
                    ) : (
                      <Button
                        data-testid={`follow-btn-${searchUser.id}`}
                        size="sm"
                        onClick={() => handleFollow(searchUser.id)}
                      >
                        Follow
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Tabs value={currentTab} onValueChange={setCurrentTab} className={styles.tabsContainer}>
          <TabsList className={styles.tabsList}>
            <TabsTrigger value="following" data-testid="tab-following">
              Following ({following.length})
            </TabsTrigger>
            <TabsTrigger value="followers" data-testid="tab-followers">
              Followers ({followers.length})
            </TabsTrigger>
            <TabsTrigger value="blocked" data-testid="tab-blocked">
              Blocked ({blocked.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="following" className={styles.tabContent}>
            {following.length === 0 ? (
              <Card className={styles.emptyCard}>
                <CardContent className={styles.emptyState}>
                  <p data-testid="empty-following-msg">You're not following anyone yet. Search for friends above!</p>
                </CardContent>
              </Card>
            ) : (
              <div className={styles.usersGrid}>
                {following.map(followUser => (
                  <Card key={followUser.id} className={`${styles.userCard} ${styles.clickableUserCard}`} data-testid={`following-${followUser.id}`}>
                    <CardContent className={styles.userCardContent} onClick={() => setSelectedUserId(followUser.id)}>
                      <Avatar className={styles.userAvatar}>
                        <AvatarImage src={followUser.picture} alt={followUser.name} />
                        <AvatarFallback>{followUser.name[0]}</AvatarFallback>
                      </Avatar>
                      <div className={styles.userDetails}>
                        <div className={styles.userName}>{followUser.name}</div>
                        <div className={styles.userUsername}>@{followUser.username}</div>
                      </div>
                      <Button
                        data-testid={`unfollow-card-btn-${followUser.id}`}
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUnfollow(followUser.id);
                        }}
                      >
                        Unfollow
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="followers" className={styles.tabContent}>
            {followers.length === 0 ? (
              <Card className={styles.emptyCard}>
                <CardContent className={styles.emptyState}>
                  <p data-testid="empty-followers-msg">No followers yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className={styles.usersGrid}>
                {followers.map(follower => (
                  <Card key={follower.id} className={`${styles.userCard} ${styles.clickableUserCard}`} data-testid={`follower-${follower.id}`}>
                    <CardContent className={styles.userCardContent} onClick={() => setSelectedUserId(follower.id)}>
                      <Avatar className={styles.userAvatar}>
                        <AvatarImage src={follower.picture} alt={follower.name} />
                        <AvatarFallback>{follower.name[0]}</AvatarFallback>
                      </Avatar>
                      <div className={styles.userDetails}>
                        <div className={styles.userName}>{follower.name}</div>
                        <div className={styles.userUsername}>@{follower.username}</div>
                      </div>
                      {isFollowing(follower.id) ? (
                        <Button
                          data-testid={`unfollow-follower-btn-${follower.id}`}
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUnfollow(follower.id);
                          }}
                        >
                          Unfollow
                        </Button>
                      ) : (
                        <Button
                          data-testid={`follow-back-btn-${follower.id}`}
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFollow(follower.id);
                          }}
                        >
                          Follow Back
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="blocked" className={styles.tabContent}>
            {blocked.length === 0 ? (
              <Card className={styles.emptyCard}>
                <CardContent className={styles.emptyState}>
                  <p data-testid="empty-blocked-msg">You haven't blocked any users.</p>
                </CardContent>
              </Card>
            ) : (
              <div className={styles.usersGrid}>
                {blocked.map(blockedUser => (
                  <Card key={blockedUser.id} className={styles.userCard} data-testid={`blocked-${blockedUser.id}`}>
                    <CardContent className={styles.userCardContent}>
                      <Avatar className={styles.userAvatar}>
                        <AvatarImage src={blockedUser.picture} alt={blockedUser.name} />
                        <AvatarFallback>{blockedUser.name[0]}</AvatarFallback>
                      </Avatar>
                      <div className={styles.userDetails}>
                        <div className={styles.userName}>{blockedUser.name}</div>
                        <div className={styles.userUsername}>@{blockedUser.username}</div>
                      </div>
                      <Button
                        data-testid={`unblock-btn-${blockedUser.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setUserToUnblock(blockedUser.id);
                          setShowUnblockConfirm(true);
                        }}
                      >
                        Unblock
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {selectedUserId && (
        <UserProfileModal 
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
          onBlockChange={() => {
            // Refresh all lists after block/unblock
            loadFollowing();
            loadFollowers();
            loadBlocked();
            // Clear search results to avoid showing blocked users
            setSearchResults([]);
          }}
        />
      )}

      {/* Unblock Confirmation Dialog */}
      <AlertDialog open={showUnblockConfirm} onOpenChange={setShowUnblockConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unblock User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unblock this user? They will be able to follow you and you will see their content again.
              <br /><br />
              <strong>Note:</strong> They will not appear in your flock unless you add them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setUserToUnblock(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnblock}>
              Unblock User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </>
  );
};

export default Friends;